<template>
  <Modal contentWidth="600px" :required="true" @close="$emit('close')">
    <template #header>
      <p class="fs-20 font-weight-bold ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
        Editar solicitud
      </p>
    </template>

    <template #body>
      <ValidationObserver v-slot="{ invalid }">
        <!-- status -->
        <ValidationProvider rules="required" name="Estado" v-slot="{ valid, errors }">
          <b-form-group :invalid-feedback="errors[0]" label-class="text-muted" class="mb-3">
            <template #label>Estado<span class="asterisk">*</span></template>
            <b-select
              v-model="form.status"
              :options="statusOptions"
              class="text-center"
              :state="errors[0] ? false : (valid ? true : null)"
            />
            <small class="text-info d-block mt-1">
              <font-awesome-icon :icon="['far', 'info-circle']" fixed-width />
              Si el estado se encuentra deshabilitado, debe cambiarlo desde el chat de la solicitud
            </small>
          </b-form-group>
        </ValidationProvider>

        <!-- document_types -->
        <b-form-group label-class="text-muted pb-1" class="mb-3">
          <template #label>Tipo de documento<span class="asterisk">*</span></template>

          <b-row class="align-items-center mb-2">
            <b-col cols="5">
              <b-checkbox v-model="includeOrder">Orden de instalación</b-checkbox>
            </b-col>
            <b-col cols="7">
              <ValidationProvider v-if="includeOrder" rules="required" name="Orden de instalación" v-slot="{ valid, errors }">
                <b-input
                  v-model="form.installation_order_number"
                  type="number"
                  min="1"
                  placeholder="Número de orden"
                  :state="errors[0] ? false : (valid ? true : null)"
                />
              </ValidationProvider>
            </b-col>
          </b-row>

          <b-row class="align-items-center mb-2">
            <b-col cols="5">
              <b-checkbox v-model="includeQuotation">Cotización</b-checkbox>
            </b-col>
            <b-col cols="7">
              <ValidationProvider v-if="includeQuotation" rules="required" name="Cotización" v-slot="{ valid, errors }">
                <b-input
                  v-model="form.quotation_number"
                  type="number"
                  min="1"
                  placeholder="Número de cotización"
                  :state="errors[0] ? false : (valid ? true : null)"
                />
              </ValidationProvider>
            </b-col>
          </b-row>

          <b-checkbox v-model="isPresale">Preventa</b-checkbox>
        </b-form-group>

        <!-- drawing_type -->
        <ValidationProvider rules="required" name="Tipo de plano" v-slot="{ valid, errors }">
          <b-form-group :invalid-feedback="errors[0]" label-class="text-muted pb-1" class="mb-3">
            <template #label>Tipo de plano<span class="asterisk">*</span></template>
            <b-select
              v-model="form.drawing_type"
              :options="drawingTypeOptions"
              class="text-center"
              :state="errors[0] ? false : (valid ? true : null)"
            />
          </b-form-group>
        </ValidationProvider>

        <!-- difficulty_level, estimated_hours -->
        <b-row v-if="canEditAdminFields">
          <b-col sm="6">
            <b-form-group label="Dificultad" label-class="text-muted pb-1" class="mb-3">
              <b-select v-model="form.difficulty_level" :options="difficultyOptions" class="text-center" />
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <ValidationProvider :rules="{ min_value: 0.5 }" name="Tiempo proyectado" v-slot="{ valid, errors }">
              <b-form-group label="Tiempo proyectado" label-class="text-muted pb-1" :invalid-feedback="errors[0]" class="mb-3">
                <b-input-group append="horas">
                  <b-input
                    v-model="form.estimated_hours"
                    type="number"
                    min="0.5"
                    step="0.5"
                    no-wheel
                    class="text-center"
                    :state="errors[0] ? false : (valid && form.estimated_hours ? true : null)"
                  />
                </b-input-group>
              </b-form-group>
            </ValidationProvider>
          </b-col>
        </b-row>

        <!-- site, business_unit -->
        <b-row v-if="canEditAdminFields">
          <b-col sm="6">
            <b-form-group label="Sede" label-class="text-muted pb-1" class="mb-3">
              <b-select v-model="form.site" :options="siteOptions" class="text-center" />
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group label="Unidad de negocio" label-class="text-muted pb-1" class="mb-3">
              <b-select v-model="form.business_unit" :options="businessUnitOptions" class="text-center" />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="text-center mt-3 mb-2">
          <Button variant="green" :disabled="invalid || !documentsValid || processing" @click="updateDrawingRequest">
            <b-spinner v-if="processing" small class="mr-1"></b-spinner>
            Guardar cambios
          </Button>
          <p v-if="errorMessage" class="text-danger fs-13 mt-2 mb-0">{{ errorMessage }}</p>
        </div>
      </ValidationObserver>
    </template>

    <template #footer>
      <span></span>
    </template>
  </Modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import Modal from '../../shared/Modal'
import Button from '../../shared/Button'
import {
  drawingTypeOptions,
  difficultyLevelOptions,
  siteOptions,
  statusOptions,
  chatOnlyStatuses
} from '../../../sharedOptions/drawingRequestOptions'

import { businessUnits } from '../../../sharedOptions/quoteBusinessUnits'


export default {
  name: 'DrawingRequestEditModal',
  components: {
    ValidationObserver,
    ValidationProvider,
    Modal,
    Button
  },
  props: {
    drawingRequest: { type: Object, required: true },
    permissions: { type: Object, default: () => ({}) }
  },
  data () {
    const request = this.drawingRequest
    const docs = request.associated_documents || []
    return {
      form: {
        status: request.status,
        drawing_type: request.drawing_type,
        installation_order_number: request.installation_order_number,
        quotation_number: request.quotation_number,
        difficulty_level: request.difficulty_level,
        estimated_hours: request.estimated_hours ? parseFloat(request.estimated_hours) : null,
        site: request.site || null,
        business_unit: request.business_unit || null
      },
      includeOrder: docs.includes('orden_instalacion'),
      includeQuotation: docs.includes('cotizacion'),
      isPresale: docs.includes('preventa'),
      processing: false,
      errorMessage: null
    }
  },
  computed: {
    canEditAdminFields () {
      return !!this.permissions.can_edit_admin_fields
    },
    statusOptions () {
      return statusOptions.map(option => ({
        ...option,
        disabled: chatOnlyStatuses.includes(option.value) && option.value !== this.drawingRequest.status
      })).sort((a, b) => a.disabled ? 1 : -1)
    },
    drawingTypeOptions () {
      return [{ value: null, text: '- Seleccione -', disabled: true }, ...drawingTypeOptions]
    },
    difficultyOptions () {
      return [{ value: null, text: 'Sin definir' }, ...difficultyLevelOptions]
    },
    siteOptions () {
      return [{ value: null, text: 'Sin definir' }, ...siteOptions]
    },
    businessUnitOptions () {
      return [{ value: null, text: 'Sin definir' }, ...businessUnits]
    },
    documentsValid () {
      return this.isPresale || this.includeOrder || this.includeQuotation
    },
    associatedDocuments () {
      if (this.isPresale) return ['preventa']

      const docs = []
      if (this.includeOrder) docs.push('orden_instalacion')
      if (this.includeQuotation) docs.push('cotizacion')
      return docs
    }
  },
  watch: {
    isPresale (value) {
      if (value) {
        this.includeOrder = false
        this.includeQuotation = false
      }
    },
    includeOrder (value) {
      if (value) this.isPresale = false
      else this.form.installation_order_number = null
    },
    includeQuotation (value) {
      if (value) this.isPresale = false
      else this.form.quotation_number = null
    }
  },
  methods: {
    updateDrawingRequest () {
      if (this.processing) return

      const payload = this.changedFields()
      if (Object.keys(payload).length === 0) {
        this.$emit('close')
        return
      }

      this.processing = true
      this.errorMessage = null

      this.$http.patch(`/api/production/drawing_requests/${this.drawingRequest.id}`, {
        drawing_request: payload
      })
        .then((res) => {
          this.$emit('updated', res.data)
        })
        .catch((error) => {
          const data = (error.response && error.response.data) || {}
          this.errorMessage = data.error || data.message || 'No se pudieron guardar los cambios'
        })
        .finally(() => {
          this.processing = false
        })
    },
    changedFields () {
      const current = this.formatPayload(this.form, this.associatedDocuments)
      const original = this.formatPayload(this.drawingRequest, this.drawingRequest.associated_documents || [])

      return Object.keys(current).reduce((changes, field) => {
        if (JSON.stringify(current[field]) !== JSON.stringify(original[field])) changes[field] = current[field]

        return changes
      }, {})
    },
    formatPayload (source, associatedDocuments) {
      const payload = {
        status: source.status,
        drawing_type: source.drawing_type,
        associated_documents: associatedDocuments,
        installation_order_number: this.toNumber(source.installation_order_number, parseInt),
        quotation_number: this.toNumber(source.quotation_number, parseInt)
      }

      if (this.canEditAdminFields) {
        payload.difficulty_level = source.difficulty_level
        payload.estimated_hours = this.toNumber(source.estimated_hours, parseFloat)
        payload.site = source.site || null
        payload.business_unit = source.business_unit || null
      }

      return payload
    },
    toNumber (value, parser) {
      return value === null || value === undefined || value === '' ? null : parser(value)
    }
  }
}
</script>
